/**
 * What kind of thing the chain names as the actor of a row.
 *
 * gdsgate writes three sorts of name into that column and they read very
 * differently once you know the shapes:
 *
 * * a **person**, as their identity provider named them — `alice@acme.example`;
 * * a **workload**, by SPIFFE ID — `spiffe://<trust domain>/<path>`, a machine
 *   that proved itself with a certificate rather than a login;
 * * an **agent** acting under somebody's delegation — a SPIFFE ID with
 *   `/delegation/` in its path, which is a person's own access, narrowed, lent
 *   to a program.
 *
 * The third is the one worth marking loudest. A row that reads like a person's
 * and is actually a program running on their behalf is the most misleading line
 * an audit trail can show.
 */
export type ActorKind = 'person' | 'workload' | 'agent';

export function actorKind(actor: string): ActorKind {
  if (!actor.startsWith('spiffe://')) return 'person';
  return actor.includes('/delegation/') ? 'agent' : 'workload';
}

/**
 * The short name to show, with the full one kept for the title.
 *
 * A SPIFFE ID is mostly trust domain and path, and the useful part is at the
 * end: `spiffe://gdsgate/user/alice/delegation/d-7f2a` is read as `d-7f2a`
 * belonging to alice. Nothing is lost — the whole id is one hover away, and the
 * row is a fraction of the width.
 */
export function actorLabel(actor: string): string {
  if (!actor.startsWith('spiffe://')) return actor;
  const parts = actor.slice('spiffe://'.length).split('/').filter((part) => part !== '');
  return parts[parts.length - 1] ?? actor;
}

/** Whose delegation an agent is acting under, when the id says so. */
export function delegatedBy(actor: string): string | undefined {
  const match = /\/user\/([^/]+)\/delegation\//.exec(actor);
  return match?.[1];
}
